import { ReactElement, useState, useEffect, useCallback } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import CloseIcon from '@mui/icons-material/Close';

import {
  getAddressLabels,
  countryUsesState,
  countryRequiresPostalCode
} from '../../constants/AddressFieldLabels';
import {
  COUNTRIES,
} from '../../constants/Country';
import Address, { AddressType, ADDRESS_TYPES } from '../../types/Address';
import Country from '../../types/Country';
import GooglePlaceField from '../common/GooglePlaceField';

interface AddressEditorProps {
  address?: Address;
  onAddressChanged?: (address: Address) => void;
  defaultCountryCode?: string;
  label?: string;
  type?: AddressType;
}

export default function AddressEditor(props: AddressEditorProps): ReactElement {
  const { onAddressChanged, defaultCountryCode = 'US', label, type = ADDRESS_TYPES.MAILING } = props;

  const [address, setAddress] = useState<Address>(
    props.address || {
      street1: '',
      city: '',
      countryCode: defaultCountryCode,
      countryName: COUNTRIES.find(c => c.code === defaultCountryCode)?.name,
      isDefault: false,
      type: type,
    }
  );
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [hasSelectedPlace, setHasSelectedPlace] = useState(!!props.address?.street1);
  const [showPOBoxField, setShowPOBoxField] = useState(false);

  const labels = getAddressLabels(address.countryCode);
  const showState = countryUsesState(address.countryCode);
  const requirePostalCode = countryRequiresPostalCode(address.countryCode);

  // Update address state when props.address changes
  useEffect(() => {
    if (props.address) {
      setAddress(props.address);
      setHasSelectedPlace(!!props.address.street1);
    }
  }, [props.address]);

  useEffect(() => {
    // Only call onAddressChanged if address has actually changed from props
    // Skip if this is just the initial mount or props update
    if (onAddressChanged && address !== props.address && address.street1) {
      onAddressChanged(address);
    }
  }, [address, onAddressChanged, props.address]);

  const handlePlaceSelected = useCallback((newAddress: Address) => {
    setAddress(newAddress);
    setHasSelectedPlace(true);
  }, []);

  const handleCountryChange = (_event: React.SyntheticEvent, country: Country | null) => {
    if (country) {
      setAddress({
        ...address,
        countryCode: country.code,
        countryName: country.name,
      });
    }
  };

  const selectedCountry = COUNTRIES.find(c => c.code === address.countryCode) || null;

  return (
    <Stack spacing={2}>
      {!showManualEntry && (
        <>
          <Stack spacing={0}>
            <GooglePlaceField
              value={address}
              label={label}
              onPlaceSelected={handlePlaceSelected}
              defaultCountryCode={defaultCountryCode}
              type={type}
              sx={{ width: '100%' }}
            />
            <Collapse in={showPOBoxField || !!address.street2}>
              <Stack direction="row" sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 2 }}>
                <TextField
                  id="street2-pobox"
                  label={labels.street2}
                  aria-label={labels.street2}
                  aria-required="false"
                  value={address.street2 || ''}
                  slotProps={{ htmlInput: { maxLength: 199 } }}
                  onChange={({ target }) =>
                    setAddress({
                      ...address,
                      street2: target.value || undefined,
                    })
                  }
                  fullWidth
                />
                {!address.street2 && (
                  <IconButton
                    size="small"
                    onClick={() => setShowPOBoxField(false)}
                    aria-label="Cancel"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Collapse>
            <Stack direction="row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {hasSelectedPlace && !showPOBoxField && !address.street2 ? (
                <Button
                  variant="text"
                  size="small"
                  
                  onClick={() => setShowPOBoxField(true)}
                  sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                >
                  Add {labels.street2}
                </Button>
              ) : (
                <Box />
              )}
              <Button
                variant="text"
                size="small"
                onClick={() => setShowManualEntry(true)}
                sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
              >
                Enter address manually
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      <Collapse in={showManualEntry}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowManualEntry(false)}
              sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
            >
              Select address
            </Button>
          </Box>

          <Autocomplete
            id="country"
            options={COUNTRIES}
            getOptionLabel={(option) => option.name}
            value={selectedCountry}
            onChange={handleCountryChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Country"
                aria-label="Country"
                aria-required="true"
              />
            )}
          />

          <TextField
            id="street1"
            label={labels.street1}
            aria-label={labels.street1}
            aria-required="true"
            value={address.street1}
            slotProps={{ htmlInput: { maxLength: 199 } }}
            onChange={({ target }) =>
              setAddress({
                ...address,
                street1: target.value,
              })
            }
          />

          <TextField
            id="street2"
            label={labels.street2}
            aria-label={labels.street2}
            aria-required="false"
            value={address.street2 || ''}
            slotProps={{ htmlInput: { maxLength: 199 } }}
            onChange={({ target }) =>
              setAddress({
                ...address,
                street2: target.value || undefined,
              })
            }
          />

          <TextField
            id="city"
            label={labels.city}
            aria-label={labels.city}
            aria-required="true"
            value={address.city}
            slotProps={{ htmlInput: { maxLength: 99 } }}
            onChange={({ target }) =>
              setAddress({
                ...address,
                city: target.value,
              })
            }
          />

          {showState && (
            <TextField
              id="state"
              label={labels.state}
              aria-label={labels.state}
              aria-required="false"
              value={address.state || ''}
              slotProps={{ htmlInput: { maxLength: 99 } }}
              onChange={({ target }) =>
                setAddress({
                  ...address,
                  state: target.value || undefined,
                })
              }
            />
          )}

          <TextField
            id="postalCode"
            label={labels.postalCode}
            aria-label={labels.postalCode}
            aria-required={requirePostalCode ? 'true' : 'false'}
            value={address.postalCode || ''}
            slotProps={{ htmlInput: { maxLength: 19 } }}
            onChange={({ target }) =>
              setAddress({
                ...address,
                postalCode: target.value || undefined,
              })
            }
          />
        </Stack>
      </Collapse>
    </Stack>
  );
}
